import * as React from 'react';
import {withRouter} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {EmailTemplate} from 'nutshell-core/settings-constants';

import {Button} from 'shells/button/button';
import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import {PageControlBar} from 'shells/control-bar/page-control-bar';
import {EditIcon, EnvelopeIcon} from 'shells/icon';
import {PageHeader} from 'shells/page-header/page-header';
import {SavedSettingToast} from 'shells/saved-setting-toast/saved-setting-toast';
import {SectionLabel} from 'shells/typography/section-label';

import type {EditionUpdateInput, EditionFragment as DripEdition} from 'nutshell-graphql-types';

import {Setting} from '../../../core/setting';
import {useGetSessionUser} from '../../../core/use-get-session-user';
import {PageContainer} from '../../../layout';

import {analyticsClient} from '../../../../analytics-client';

import {getEditionStatusBarInfo} from '../../editions/edition-helpers';

import {DripEditionsDetailsForm} from './drip-editions-details-form';
import {DripSequenceEditionTabContent} from './drip-sequence-edition-tab-content';
import {PublishChangesModal} from './publish-changes-modal';

import styles from './drip-sequence-edition-page.module.css';

type OwnProps = {
    edition: DripEdition;
    onDiscardChanges: () => void;
    onPublishChanges: (saveData: EditionUpdateInput) => Promise<any>;
};

type Props = OwnProps & ContextRouter;

export function DripEditionEditModeComponent(props: Props) {
    const [activeEditingTab, setActiveEditingTab] = React.useState<string>('editor');
    const [isSavingChanges, setIsSavingChanges] = React.useState<boolean>(false);
    const {edition} = props;

    const [shouldShowDiscardChangesDialog, setShouldShowDiscardChangesDialog] =
        React.useState<boolean>(false);
    const [shouldShowPublishChangesModal, setShouldShowPublishChangesModal] =
        React.useState<boolean>(false);

    // Tracking unsaved changes made to editions that are currently active
    const [unsavedDetails, setUnsavedDetails] = React.useState<
        EditionUpdateInput | null | undefined
    >(undefined);
    const [unsavedMessageJson, setUnsavedMessageJson] = React.useState<string | null | undefined>(
        undefined
    );

    const hasUnsavedChanges = Boolean(unsavedMessageJson || unsavedDetails);

    const {user} = useGetSessionUser();

    function handlePublishingChanges() {
        setIsSavingChanges(true);

        let saveData: Record<string, any> = {};

        // Save message json if it has been updated
        if (unsavedMessageJson) {
            saveData.beeBodyJson = unsavedMessageJson;
        }

        // Update edition details if changes have been made there
        if (unsavedDetails) {
            saveData = {
                ...saveData,
                ...unsavedDetails,
            };
        }

        props
            .onPublishChanges({
                editionId: edition.id,
                ...saveData,
            })
            .then(() => {
                analyticsClient.track(
                    analyticsClient.EVENTS.TRACK_DRIP_SEQUENCE_ACTIVE_EDITION_EDITED
                );

                setIsSavingChanges(false);
                setShouldShowPublishChangesModal(false);
            });
    }

    const tabOptions = [
        {
            id: 'editor',
            label: 'Editor',
            icon: EditIcon,
        },
        {
            id: 'details',
            label: 'Details',
            icon: EnvelopeIcon,
            component: edition && (
                <div className={styles['details-container']}>
                    <SectionLabel>
                        <SavedSettingToast state='initial'>Message details</SavedSettingToast>
                    </SectionLabel>
                    {props.edition ? (
                        <DripEditionsDetailsForm
                            edition={props.edition}
                            onEditionSave={(saveData) => {
                                // Nothing saved, but we expect a Promise
                                return new Promise(() => {
                                    setUnsavedDetails(saveData);
                                });
                            }}
                            isReadOnly={false}
                        />
                    ) : undefined}
                </div>
            ),
        },
    ];

    const isOwner = Boolean(
        user &&
            props.edition &&
            props.edition.campaign &&
            props.edition.campaign.owner &&
            props.edition.campaign.owner.id === user.id
    );

    return (
        <PageContainer
            title={`You’re editing ${props.edition.name}`}
            pageTitleBar={
                <PageHeader
                    backButton={{
                        onClick: () => {
                            if (hasUnsavedChanges) {
                                setShouldShowDiscardChangesDialog(true);
                            } else {
                                props.onDiscardChanges();
                            }
                        },
                        text: 'Discard changes',
                    }}
                    title={`You’re editing ${props.edition.name}`}
                    subtitle='Changes won’t take effect until published'
                    actionButtons={
                        <Button
                            variant='primary'
                            size='big'
                            onClick={() => {
                                setShouldShowPublishChangesModal(true);
                            }}
                            disabled={!hasUnsavedChanges}
                        >
                            Publish changes
                        </Button>
                    }
                    // @ts-expect-error
                    controlBar={
                        <PageControlBar
                            statusInfo={getEditionStatusBarInfo(
                                props.edition,
                                Boolean(user && user.permissions.canAccessFullCampaigns),
                                undefined,
                                false,
                                isOwner
                            )}
                            tabs={{
                                selectedTab: activeEditingTab,
                                options: [
                                    {
                                        id: 'editor',
                                        label: 'Editor',
                                        icon: EditIcon,
                                        href: '#',
                                    },
                                    {
                                        id: 'details',
                                        label: 'Details',
                                        icon: EnvelopeIcon,
                                        href: '#',
                                    },
                                ],
                                onChangeTab: (newTab) => {
                                    setActiveEditingTab(newTab);
                                },
                            }}
                        />
                    }
                />
            }
        >
            <DripSequenceEditionTabContent
                activeTab={activeEditingTab}
                tabOptions={tabOptions}
                edition={props.edition}
                isLoading={false}
                onBeeEditorContentChange={(editionId: string, beeEditorJson: string) => {
                    return new Promise(() => {
                        // Update unsaved changes
                        setUnsavedMessageJson(beeEditorJson);
                    });
                }}
                canMakeChanges={true}
            />
            {shouldShowDiscardChangesDialog ? (
                <ConfirmationDialog
                    title='Discard unsaved changes'
                    message='If you cancel now, you will lose your changes. Are you sure you want to proceed?'
                    cancelText='No, don’t cancel'
                    confirmText='Yes, cancel changes'
                    onCancel={() => {
                        setShouldShowDiscardChangesDialog(false);
                    }}
                    onConfirm={() => {
                        props.onDiscardChanges();
                        setShouldShowDiscardChangesDialog(false);
                    }}
                />
            ) : undefined}
            <Setting settingName={EmailTemplate.STREET_ADDRESS_SETTING_NAME}>
                {({setting, isLoading}) =>
                    shouldShowPublishChangesModal && props.edition ? (
                        <PublishChangesModal
                            // TODO: Edition object being passed is not updated with unsaved changes
                            edition={props.edition}
                            // Passing unsaved details to check some unsaved changes (subject & previewText)
                            unsavedDetails={unsavedDetails}
                            streetAddress={setting ? setting.value : ''}
                            canAccessSetup={Boolean(user && user.permissions.canAccessSetup)}
                            isLoading={isLoading}
                            isSaving={isSavingChanges}
                            onPublishChanges={handlePublishingChanges}
                            onModalClose={() => {
                                setShouldShowPublishChangesModal(false);
                            }}
                        />
                    ) : (
                        <></>
                    )
                }
            </Setting>
        </PageContainer>
    );
}

// @ts-expect-error
export const DripEditionEditMode = withRouter<OwnProps>(DripEditionEditModeComponent);
